import { ArrowUpRight, Check, CircleCheck, Clock3, ExternalLink, X } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import { formatMoney } from '@/utility/money';
import { showNotification } from '@/utility/showNotification';
import { hasWalletActivity } from '@/utility/walletDisplay';
import { formatBundleEarning, isBundle } from '@/utility/contracts';
import { useModalFocus } from '@/hooks/useModalFocus';
import { handleTabKeyDown } from '@/utility/tabKeyboard';

/**
 * Creator-portal Wallet page, ported from the prototype's WalletPage (ROU-1192)
 * and wired to the real wallet API. Per-currency balances (me/wallet/balance/),
 * earnings (me/wallet/earnings/) and withdrawals (me/wallet/withdrawals/) are
 * live, as are the display-currency converted totals returned alongside the
 * balances. "Withdraw" cashes out everything at once: the server converts the
 * creator's full available balance across all currencies into their display
 * currency at the current rate and opens one withdrawal. Unwithdrawn money is only
 * ever shown in the display currency, converted the same way the withdrawal itself
 * converts it (FX buffer included), so the available figure is the amount the
 * creator will be paid. History rows below stay in the currency each campaign paid.
 * It stays closed until the creator has saved a PayPal email on Account, since that
 * is the destination the payout is sent to.
 *
 * "Total earned" counts pending alongside settled (ROU-1504): accrued work has been
 * earned, it just hasn't cleared, so the figure never contradicts the pending one
 * beside it. "Available to withdraw" stays cleared-money-only.
 */

const SUPPORT_EMAIL = 'hello@wave.me';

const EARNING_STATE_LABELS: Record<wave.WalletEarningState, string> = {
    pending_settlement: 'Pending',
    available: 'Available',
    withdrawing: 'Withdrawing',
    paid: 'Complete',
    reversed: 'Reversed',
};

const WITHDRAWAL_STATUS_LABELS: Record<wave.WalletWithdrawalStatus, string> = {
    requested: 'Requested',
    processing: 'Processing',
    paid: 'Paid',
    failed: 'Failed',
    cancelled: 'Cancelled',
};

// The prototype only distinguished "Complete" (settled) from everything else.
const COMPLETE_EARNING_STATES: wave.WalletEarningState[] = ['available', 'paid'];

const formatDate = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

type HistoryTab = 'earnings' | 'withdrawals';
const HISTORY_TABS: HistoryTab[] = ['earnings', 'withdrawals'];

const EarningRow = ({ earning, bundle }: { earning: wave.WalletEarning; bundle?: wave.Contract }) => {
    const isComplete = COMPLETE_EARNING_STATES.includes(earning.state);
    return (
        <div className="earning-row">
            <span className={`earning-row__icon ${isComplete ? '' : 'is-pending'}`}>
                {isComplete ? <CircleCheck size={16} /> : <Clock3 size={16} />}
            </span>
            <span className="earning-row__details">
                {earning.post_url ? (
                    <a href={earning.post_url} target="_blank" rel="noreferrer">
                        <b>{earning.campaign_name || 'Campaign payment'}</b>
                        <ExternalLink size={11} />
                    </a>
                ) : (
                    <b>{earning.campaign_name || 'Campaign payment'}</b>
                )}
                {bundle ? <small>{formatBundleEarning(bundle)}</small> : null}
                <small>{formatDate(earning.earned_at)}</small>
            </span>
            <span className="earning-row__amount">
                <b>{formatMoney(earning.currency.symbol, earning.amount)}</b>
                <small className={isComplete ? 'is-complete' : 'is-pending'}>
                    {isComplete && <Check size={11} />}
                    <span>{EARNING_STATE_LABELS[earning.state] ?? earning.state}</span>
                </small>
            </span>
        </div>
    );
};

const PendingPostRow = ({ post }: { post: wave.WalletPendingPost }) => {
    return (
        <div className="earning-row">
            <span className="earning-row__icon is-pending">
                <Clock3 size={16} />
            </span>
            <span className="earning-row__details">
                <a href={post.post_url} target="_blank" rel="noreferrer">
                    <b>{post.campaign_name}</b>
                    <ExternalLink size={11} />
                </a>
                <small>{formatDate(post.updated_at)}</small>
            </span>
            <span className="earning-row__amount">
                <b>{formatMoney(post.currency.symbol, post.amount)}</b>
                <small className="is-pending">Pending</small>
            </span>
        </div>
    );
};

const WithdrawalRow = ({ withdrawal }: { withdrawal: wave.WalletWithdrawal }) => {
    const symbol = withdrawal.currency?.symbol ?? '';
    return (
        <div className="earning-row">
            <span className="earning-row__icon is-pending">
                <ArrowUpRight size={16} />
            </span>
            <span className="earning-row__details">
                <b>PayPal withdrawal</b>
                <small>{withdrawal.requested_at ? formatDate(withdrawal.requested_at) : '-'}</small>
                {/* Null until the payout has left, so an in-flight withdrawal shows no line at all. */}
                {withdrawal.paypal_reference && (
                    <small className="earning-row__reference">Ref {withdrawal.paypal_reference}</small>
                )}
            </span>
            <span className="earning-row__amount">
                <b>{symbol ? formatMoney(symbol, withdrawal.amount) : withdrawal.amount}</b>
                <small className="is-pending">{WITHDRAWAL_STATUS_LABELS[withdrawal.status] ?? withdrawal.status}</small>
            </span>
        </div>
    );
};

const WalletPage = () => {
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<HistoryTab>('earnings');
    const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);

    const balancesQuery = useQuery(waveQueries.wallet.options.balances());
    // A bundle earns once for the whole set, with no post to link, so its row reads its terms from the contract.
    const contractsQuery = useQuery(waveQueries.contract.options.mine());
    const bundlesById = new Map(
        (contractsQuery.data ?? []).filter(isBundle).map((contract) => [contract.id, contract])
    );
    const earningsQuery = useInfiniteQuery(waveQueries.wallet.options.earnings());
    const withdrawalsQuery = useInfiniteQuery(waveQueries.wallet.options.withdrawals());

    const balances = balancesQuery.data?.balances ?? [];
    const totals = balancesQuery.data?.totals ?? null;
    const pendingPosts = balancesQuery.data?.pending_posts ?? [];
    const earnings = earningsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    const withdrawals = withdrawalsQuery.data?.pages.flatMap((page) => page.results) ?? [];

    const displayCurrency = totals?.display_currency ?? null;
    const displaySymbol = displayCurrency?.symbol ?? '';
    const displayName = displayCurrency?.name ?? 'your currency';
    const availableTotal = totals ? Number(totals.available) : 0;
    // A withdrawal needs somewhere to send the money, and the server rejects one without
    // it, so the action stays closed until the creator saves a PayPal email on Account.
    const hasPayoutDestination = totals?.has_payout_destination ?? false;
    const canWithdraw = availableTotal > 0 && hasPayoutDestination;
    // The header totals are converted; the rows below are not. Say so when they can
    // disagree, so the same money in two currencies doesn't read as two amounts.
    const hasForeignBalance = balances.some((balance) => balance.currency.id !== displayCurrency?.id);
    const walletHasActivity = hasWalletActivity({
        liveBalanceCount: balances.length,
        pendingPostCount: pendingPosts.length,
        lifetimeEarnings: totals?.lifetime ?? 0,
    });

    // The whole balance leaves the wallet in one request, so a single in-flight
    // flag guards the one action rather than a per-currency one.
    const closeWithdraw = () => {
        if (isWithdrawing) {
            return;
        }
        setIsWithdrawOpen(false);
    };
    const withdrawModalRef = useModalFocus<HTMLDivElement>(isWithdrawOpen, closeWithdraw);

    const handleWithdrawAll = async () => {
        if (isWithdrawing) {
            return;
        }
        setIsWithdrawing(true);
        try {
            const response = await wave.createWithdrawal();

            if (response.status === 201) {
                showNotification('Withdrawal requested. We’ll email you when it’s paid.', 'success');
                await queryClient.invalidateQueries({ queryKey: waveQueries.wallet.keys.all() });
                setIsWithdrawOpen(false);
                setTab('withdrawals');
                return;
            }

            const message = response.data[0] ?? 'Couldn’t request your withdrawal.';
            showNotification(message, 'error');
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } finally {
            setIsWithdrawing(false);
        }
    };

    return (
        <div className="page wallet-page">
            <header className="page-heading">
                <div>
                    <h1>Wallet</h1>
                    <p>What you’ve earned, and what you’ve withdrawn.</p>
                </div>
            </header>

            {balancesQuery.isLoading ? (
                <section className="wallet-empty-inline">
                    <p>Loading your balance…</p>
                </section>
            ) : balancesQuery.isError || !balancesQuery.data ? (
                <section className="wallet-empty-inline">
                    <h3>Couldn’t load your wallet</h3>
                    <p>Something went wrong. Try again shortly.</p>
                </section>
            ) : walletHasActivity ? (
                <section className="wallet-summary">
                    <div className="wallet-summary__main">
                        <div className="wallet-summary__topline">
                            <span>Available to withdraw</span>
                            <span>{displayName}</span>
                        </div>
                        <div className="wallet-summary__balance-row">
                            <strong>{formatMoney(displaySymbol, availableTotal)}</strong>
                            <button
                                className="wallet-withdraw-button"
                                type="button"
                                onClick={() => setIsWithdrawOpen(true)}
                                disabled={!canWithdraw || isWithdrawing}
                            >
                                <ArrowUpRight size={14} /> <span>Withdraw to {displayName}</span>
                            </button>
                        </div>
                        {!hasPayoutDestination && (
                            <small>
                                Add your PayPal email on your Account page to withdraw — that’s where we send your
                                money.
                            </small>
                        )}
                        {hasForeignBalance && (
                            <small>
                                Totals are converted to {displayName} at today’s rate. History below shows the currency
                                each campaign paid in.
                            </small>
                        )}
                        {totals?.partial && (
                            <small>
                                Some balances can’t be converted to {displayName} right now and aren’t included in this
                                total.
                            </small>
                        )}
                    </div>
                    <div>
                        <span>Pending</span>
                        <strong>{formatMoney(displaySymbol, totals?.pending ?? 0)}</strong>
                        <small>Approved work and active CPM earnings</small>
                    </div>
                    <div>
                        <span>Total earned</span>
                        <strong>{formatMoney(displaySymbol, totals?.lifetime ?? 0)}</strong>
                        <small>Settled and pending together</small>
                    </div>
                </section>
            ) : (
                <section className="wallet-empty-inline">
                    <h3>No balance yet</h3>
                    <p>Your earnings from approved posts will show up here.</p>
                </section>
            )}

            <section className="history-section">
                <h2>History</h2>
                <div className="segmented-control" role="tablist" aria-label="Wallet history">
                    <button
                        type="button"
                        role="tab"
                        id="wallet-earnings-tab"
                        aria-selected={tab === 'earnings'}
                        aria-controls="wallet-history-panel"
                        tabIndex={tab === 'earnings' ? 0 : -1}
                        className={tab === 'earnings' ? 'is-active' : ''}
                        onClick={() => setTab('earnings')}
                        onKeyDown={(event) => handleTabKeyDown(event, HISTORY_TABS, tab, setTab)}
                    >
                        Earnings
                    </button>
                    <button
                        type="button"
                        role="tab"
                        id="wallet-withdrawals-tab"
                        aria-selected={tab === 'withdrawals'}
                        aria-controls="wallet-history-panel"
                        tabIndex={tab === 'withdrawals' ? 0 : -1}
                        className={tab === 'withdrawals' ? 'is-active' : ''}
                        onClick={() => setTab('withdrawals')}
                        onKeyDown={(event) => handleTabKeyDown(event, HISTORY_TABS, tab, setTab)}
                    >
                        Withdrawals
                    </button>
                </div>

                <div id="wallet-history-panel" role="tabpanel" aria-labelledby={`wallet-${tab}-tab`}>
                    {tab === 'earnings' ? (
                        <HistoryList
                            isLoading={earningsQuery.isLoading || balancesQuery.isLoading}
                            isError={earningsQuery.isError}
                            isEmpty={pendingPosts.length === 0 && earnings.length === 0}
                            emptyTitle="No earnings yet"
                            emptyBody="Approved campaign earnings will appear here."
                            hasNextPage={Boolean(earningsQuery.hasNextPage)}
                            isFetchingNextPage={earningsQuery.isFetchingNextPage}
                            onLoadMore={() => earningsQuery.fetchNextPage()}
                        >
                            {pendingPosts.map((post) => (
                                <PendingPostRow key={`pending-${post.id}`} post={post} />
                            ))}
                            {earnings.map((earning) => (
                                <EarningRow
                                    key={earning.id}
                                    earning={earning}
                                    bundle={
                                        earning.contract_id === null ? undefined : bundlesById.get(earning.contract_id)
                                    }
                                />
                            ))}
                        </HistoryList>
                    ) : (
                        <HistoryList
                            isLoading={withdrawalsQuery.isLoading}
                            isError={withdrawalsQuery.isError}
                            isEmpty={withdrawals.length === 0}
                            emptyTitle="No withdrawals yet"
                            emptyBody="Your completed withdrawals will appear here."
                            hasNextPage={Boolean(withdrawalsQuery.hasNextPage)}
                            isFetchingNextPage={withdrawalsQuery.isFetchingNextPage}
                            onLoadMore={() => withdrawalsQuery.fetchNextPage()}
                        >
                            {withdrawals.map((withdrawal, index) => (
                                <WithdrawalRow key={withdrawal.id ?? index} withdrawal={withdrawal} />
                            ))}
                        </HistoryList>
                    )}
                </div>
            </section>

            {isWithdrawOpen && (
                <div className="modal-backdrop" onMouseDown={closeWithdraw}>
                    <div
                        ref={withdrawModalRef}
                        className="modal-card"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="withdraw-title"
                        aria-describedby="withdraw-description"
                        tabIndex={-1}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <button
                            className="modal-card__close"
                            type="button"
                            aria-label="Close withdrawal"
                            onClick={closeWithdraw}
                            disabled={isWithdrawing}
                        >
                            <X size={17} />
                        </button>
                        <h2 id="withdraw-title">Withdraw to {displayName}</h2>
                        <p id="withdraw-description">
                            This converts your full available balance into {displayName} at today’s exchange rate and
                            sends it to your PayPal.
                        </p>
                        <div className="withdrawal-breakdown" aria-live="polite">
                            <span className="withdrawal-breakdown__total">
                                <small>You’ll receive</small>
                                <strong>{formatMoney(displaySymbol, availableTotal)}</strong>
                            </span>
                            {totals?.partial && (
                                <small>
                                    Balances we can’t convert to {displayName} right now aren’t part of this amount and
                                    stay in your wallet.
                                </small>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="button button--primary"
                                type="button"
                                onClick={handleWithdrawAll}
                                disabled={isWithdrawing}
                            >
                                {isWithdrawing ? 'Withdrawing…' : 'Confirm withdrawal'}
                            </button>
                            <button
                                className="button button--secondary"
                                type="button"
                                onClick={closeWithdraw}
                                disabled={isWithdrawing}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

type HistoryListProps = {
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
    emptyTitle: string;
    emptyBody: string;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    children: ReactNode;
};

const HistoryList = ({
    isLoading,
    isError,
    isEmpty,
    emptyTitle,
    emptyBody,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    children,
}: HistoryListProps) => {
    if (isLoading) {
        return (
            <div className="wallet-empty-inline">
                <p>Loading…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="wallet-empty-inline">
                <h3>Couldn’t load this</h3>
                <p>Try again shortly.</p>
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div className="wallet-empty-inline">
                <h3>{emptyTitle}</h3>
                <p>{emptyBody}</p>
            </div>
        );
    }

    return (
        <>
            <div className="earning-list">{children}</div>
            {hasNextPage && (
                <div className="modal-actions">
                    <button
                        className="button button--secondary"
                        type="button"
                        disabled={isFetchingNextPage}
                        onClick={onLoadMore}
                    >
                        {isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </button>
                </div>
            )}
        </>
    );
};

export default WalletPage;
